/**
 * Regex to match variable patterns in resolver keys.
 * Matches both {varName} and {varName=value}
 */
const VAR_PATTERN = /\{([^}=]+)(?:=([^}]*))?\}/g;

/**
 * Extract bound variables from a resolver key.
 * e.g., 'ui.{lang=en}.events.{slug}' → { lang: 'en' }
 */
export function extractBoundVarsFromKey(key: string): Record<string, string> {
  const vars: Record<string, string> = {};
  let match;
  VAR_PATTERN.lastIndex = 0; // Reset global regex state
  while ((match = VAR_PATTERN.exec(key)) !== null) {
    const [, varName, varValue] = match;
    if (varValue !== undefined) {
      vars[varName] = varValue;
    }
  }
  return vars;
}

/**
 * Extract all variable names from a resolver key (both bound and unbound).
 * e.g., 'ui.{lang=en}.events.{slug}' → ['lang', 'slug']
 */
export function extractAllVarNamesFromKey(key: string): string[] {
  const varNames: string[] = [];
  let match;
  VAR_PATTERN.lastIndex = 0; // Reset global regex state
  while ((match = VAR_PATTERN.exec(key)) !== null) {
    varNames.push(match[1]);
  }
  return varNames;
}

/**
 * Create a fully resolved key with all variables bound.
 * e.g., resolveKey('ui.{lang}.events.{slug}', { lang: 'en', slug: 'featured' })
 *       → 'ui.{lang=en}.events.{slug=featured}'
 */
export function resolveKey(key: string, vars: Record<string, string>): string {
  return key.replace(VAR_PATTERN, (match, varName, existingValue) => {
    // If already bound, keep the existing value
    if (existingValue !== undefined) {
      return match;
    }
    // If we have a value for this var, bind it
    const value = vars[varName];
    if (value !== undefined) {
      return `{${varName}=${value}}`;
    }
    // Keep unbound
    return match;
  });
}

/**
 * Count the number of bound variables in a key.
 * e.g., 'ui.{lang=en}.events.{slug}' → 1
 * e.g., 'ui.{lang=en}.events.{slug=featured}' → 2
 */
export function countBoundVars(key: string): number {
  let count = 0;
  let match;
  VAR_PATTERN.lastIndex = 0; // Reset global regex state
  while ((match = VAR_PATTERN.exec(key)) !== null) {
    if (match[2] !== undefined) {
      count++;
    }
  }
  return count;
}

/**
 * Generate all key variations from most specific (all vars bound) to least specific (all vars unbound).
 * This is used to search the database in specificity order.
 *
 * @param key - The resolver key pattern (may have some vars already bound)
 * @param vars - The variables to bind (from opts.vars, context, and key-bound vars merged)
 * @returns Array of key variations sorted by specificity (most specific first)
 */
export function generateSpecificityVariations(
  key: string,
  vars: Record<string, string>,
): string[] {
  const varNames = extractAllVarNamesFromKey(key);
  const boundFromKey = extractBoundVarsFromKey(key);

  // Merge key-bound vars with provided vars (key-bound takes precedence but they should be same)
  const allVars = { ...vars, ...boundFromKey };

  // Normalize the key to use unbound format for all variables
  const normalizedKey = key.replace(VAR_PATTERN, (_, varName) => `{${varName}}`);

  // Generate all combinations of bound/unbound for each variable
  const combinations: Array<Record<string, string | undefined>> = [];
  const numVars = varNames.length;
  const numCombinations = Math.pow(2, numVars);

  for (let i = 0; i < numCombinations; i++) {
    const combo: Record<string, string | undefined> = {};
    for (let j = 0; j < numVars; j++) {
      const varName = varNames[j];
      // Bit j is set means this var is bound
      const isBound = (i >> j) & 1;
      combo[varName] = isBound ? allVars[varName] : undefined;
    }
    combinations.push(combo);
  }

  // Sort by number of bound vars (descending) - more bound = more specific
  combinations.sort((a, b) => {
    const countA = Object.values(a).filter((v) => v !== undefined).length;
    const countB = Object.values(b).filter((v) => v !== undefined).length;
    return countB - countA;
  });

  // Generate the key strings
  return combinations.map((combo) => {
    return normalizedKey.replace(VAR_PATTERN, (_, varName) => {
      const value = combo[varName];
      return value !== undefined ? `{${varName}=${value}}` : `{${varName}}`;
    });
  });
}

/**
 * Check if a candidate key could resolve to the target key.
 * A candidate matches if all its bound variables match the target's bound variables,
 * and it doesn't bind variables to different values than the target.
 *
 * e.g., 'ui.{lang=en}.events.{slug}' matches target 'ui.{lang=en}.events.{slug=featured}'
 * e.g., 'ui.{lang=fr}.events.{slug}' does NOT match target 'ui.{lang=en}.events.{slug=featured}'
 */
export function keyMatchesTarget(candidateKey: string, targetKey: string): boolean {
  const candidateBound = extractBoundVarsFromKey(candidateKey);
  const targetBound = extractBoundVarsFromKey(targetKey);

  // Check that all bound vars in candidate match the target
  for (const [varName, value] of Object.entries(candidateBound)) {
    if (targetBound[varName] !== undefined && targetBound[varName] !== value) {
      return false;
    }
  }

  return true;
}
